import { useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Checkbox,
	DatePicker,
	Input,
	Modal,
	Select,
	Space,
	Table,
	Tabs,
	message,
	Popconfirm,
	Tag,
} from "antd";
import dayjs from "dayjs";
import {
	DeleteOutlined,
	FilePdfOutlined,
	PlusOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import pozyczkiGetList from "./data/pozyczkiGetList";
import pozyczkiGetData from "./data/pozyczkiGetData";
import pozyczkiUpdateData from "./data/pozyczkiUpdateData";
import pozyczkiImportXml from "./data/pozyczkiImportXml";
import pozyczkiAddComment from "./data/pozyczkiAddComment";
import pozyczkiAddManualRepayment from "./data/pozyczkiAddManualRepayment";
import pozyczkiAddPromissoryNote from "./data/pozyczkiAddPromissoryNote";
import pozyczkiDeleteActivePromissoryNote from "./data/pozyczkiDeleteActivePromissoryNote";
import pozyczkiGeneratePromissoryPdf from "./data/pozyczkiGeneratePromissoryPdf";
import { useAuth } from "../../context/AuthContext";
import FixedNumberInput from "../../components/FixedNumberInput";
import formatPrice from "../../components/formatPrice";

const createTempId = () =>
	`temp_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

const createOwnId = () => Math.floor(Date.now() / 1000);

const toUnixTimestamp = (dateString) => {
	const normalized = String(dateString || "").trim();
	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
		return Math.floor(Date.now() / 1000);
	}
	const ts = Math.floor(new Date(`${normalized}T00:00:00`).getTime() / 1000);
	return Number.isFinite(ts) && ts > 0 ? ts : Math.floor(Date.now() / 1000);
};

const getLoanAutoNumberPreview = (loanData) => {
	const ownId = Number(loanData?.msr_loans_own_id || 0);
	if (Number.isFinite(ownId) && ownId > 0) {
		return `POZ-${Math.floor(ownId)}`;
	}
	return "";
};

const getPromissoryAutoNumberPreview = (loanData) => {
	const ownId = Number(loanData?.msr_loans_own_id || 0);
	const promissoryDate = String(
		loanData?.msr_loans_promissory_note_date || "",
	).trim();
	if (!ownId || !promissoryDate) {
		return "";
	}
	return `POZYCZKA-${Math.floor(ownId)}-${toUnixTimestamp(promissoryDate)}`;
};

const PURPOSE_REFERENCE_TEXT =
	"opłata od skargi na orzeczenie referendarza o wynagrodzeniu biegłych";
const LOAN_PURPOSE_LIMIT = PURPOSE_REFERENCE_TEXT.length + 50;
const CASE_STATUS_OPTIONS = [
	{ value: "SPRAWA W TOKU", label: "SPRAWA W TOKU" },
	{ value: "NIEOKREŚLONY", label: "NIEOKREŚLONY" },
	{ value: "ZAKOŃCZONA", label: "ZAKOŃCZONA" },
];

const createManualRepayment = () => ({
	settlementDate: dayjs().format("YYYY-MM-DD"),
	amount: 0,
	description: "",
});

const isDateBefore = (left, right) => {
	if (!left || !right) return false;
	return String(left).trim() < String(right).trim();
};

const Pozyczki = () => {
	const { user } = useAuth();
	const navigate = useNavigate();

	const [pagination, setPagination] = useState({
		current: 1,
		pagesize: 20,
		search: "",
		loanDateFrom: "",
		loanDateTo: "",
		annexDateFrom: "",
		annexDateTo: "",
		promissoryNoteDateFrom: "",
		promissoryNoteDateTo: "",
	});

	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);

	const [modalOpen, setModalOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [importLoading, setImportLoading] = useState(false);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [commentSaving, setCommentSaving] = useState(false);
	const [promissorySaving, setPromissorySaving] = useState(false);
	const [deletePromissoryLoading, setDeletePromissoryLoading] =
		useState(false);
	const [manualRepaymentSaving, setManualRepaymentSaving] = useState(false);
	const [activeTab, setActiveTab] = useState("loan");
	const xmlInputRef = useRef(null);

	const [loan, setLoan] = useState(null);
	const [repayments, setRepayments] = useState([]);
	const [promissoryNotes, setPromissoryNotes] = useState([]);
	const [comments, setComments] = useState([]);
	const [newComment, setNewComment] = useState("");
	const [newPromissory, setNewPromissory] = useState({
		date: null,
	});
	const [manualRepayment, setManualRepayment] = useState(
		createManualRepayment,
	);
	const [borrower, setBorrower] = useState({
		msr_loan_borrowers_name: "",
		msr_loan_borrowers_lastname: "",
		msr_loan_borrowers_pesel: "",
	});

	const isNewLoan = useMemo(() => {
		if (!loan?.msr_loans_id) return true;
		return String(loan.msr_loans_id).includes("temp_");
	}, [loan?.msr_loans_id]);

	const isPromissorySkipped =
		Number(loan?.msr_loans_skip_promissory_note) === 1;

	const fetchList = async () => {
		setLoading(true);
		try {
			const response = await pozyczkiGetList(pagination);
			if (response?.data?.status) {
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
			} else {
				message.error(
					response?.data?.message ||
						"Nie udało się pobrać listy pożyczek",
				);
			}
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - lista pożyczek");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		pagination.current,
		pagination.pagesize,
		pagination.search,
		pagination.loanDateFrom,
		pagination.loanDateTo,
		pagination.annexDateFrom,
		pagination.annexDateTo,
		pagination.promissoryNoteDateFrom,
		pagination.promissoryNoteDateTo,
	]);

	useEffect(() => {
		if (isPromissorySkipped && activeTab === "promissory") {
			setActiveTab("loan");
		}
	}, [activeTab, isPromissorySkipped]);

	const openNew = () => {
		setActiveTab("loan");
		const ownId = createOwnId();
		setLoan({
			msr_loans_id: createTempId(),
			msr_loans_own_id: ownId,
			msr_loans_date: dayjs().format("YYYY-MM-DD"),
			msr_loans_amount: 0,
			msr_loans_custom_number: "",
			msr_loans_grant_date: null,
			msr_loans_purpose: "",
			msr_loans_archive_location: "",
			msr_loans_case_status: "NIEOKREŚLONY",
			msr_loans_is_foundation_funding: 0,
			msr_loans_annex_date: null,
			msr_loans_promissory_note_date: dayjs().format("YYYY-MM-DD"),
			msr_loans_promissory_note_number: "",
			msr_loans_skip_promissory_note: 0,
			msr_loans_case_signature: "",
			msr_loans_description: "",
		});
		setBorrower({
			msr_loan_borrowers_name: "",
			msr_loan_borrowers_lastname: "",
			msr_loan_borrowers_pesel: "",
		});
		setRepayments([]);
		setPromissoryNotes([]);
		setComments([]);
		setNewComment("");
		setNewPromissory({ date: null });
		setManualRepayment(createManualRepayment());
		setModalOpen(true);
	};

	const loadLoanDetails = async (id) => {
		const response = await pozyczkiGetData(id);
		if (!response?.data?.status) {
			message.error(
				response?.data?.message ||
					"Nie udało się pobrać danych pożyczki",
			);
			return false;
		}
		setLoan(response.data.data.loan);
		setBorrower(
			response.data.data.borrower || {
				msr_loan_borrowers_name: "",
				msr_loan_borrowers_lastname: "",
				msr_loan_borrowers_pesel: "",
			},
		);
		setRepayments(response.data.data.repayments || []);
		setPromissoryNotes(response.data.data.promissoryNotes || []);
		setComments(response.data.data.comments || []);
		return true;
	};

	const openEdit = async (id) => {
		setActiveTab("loan");
		setLoading(true);
		try {
			const loaded = await loadLoanDetails(id);
			if (!loaded) return;
			setNewComment("");
			setNewPromissory({ date: null });
			setManualRepayment(createManualRepayment());
			setModalOpen(true);
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - dane pożyczki");
		} finally {
			setLoading(false);
		}
	};

	const save = async () => {
		if (!loan) return;
		const borrowerName = String(
			borrower?.msr_loan_borrowers_name ?? "",
		).trim();
		const borrowerLastname = String(
			borrower?.msr_loan_borrowers_lastname ?? "",
		).trim();
		const loanDate = String(loan?.msr_loans_date ?? "").trim();
		const grantDate = String(loan?.msr_loans_grant_date ?? "").trim();
		const promissoryDate = String(
			loan?.msr_loans_promissory_note_date ?? "",
		).trim();
		const skipPromissory =
			Number(loan?.msr_loans_skip_promissory_note) === 1;
		const loanAmountNumber = Number(loan?.msr_loans_amount);
		const purpose = String(loan?.msr_loans_purpose ?? "").trim();

		if (!borrowerName || !borrowerLastname) {
			message.error("Uzupełnij pożyczkobiorcę (imię i nazwisko)");
			setActiveTab("loan");
			return;
		}
		if (!loanDate) {
			message.error("Data pożyczki jest wymagana");
			setActiveTab("loan");
			return;
		}
		if (!grantDate) {
			message.error("Data przyznania jest wymagana");
			setActiveTab("loan");
			return;
		}
		if (!skipPromissory && !promissoryDate) {
			message.error("Data weksla jest wymagana");
			setActiveTab("loan");
			return;
		}
		if (!Number.isFinite(loanAmountNumber) || loanAmountNumber <= 0) {
			message.error("Kwota pożyczki jest wymagana");
			setActiveTab("loan");
			return;
		}
		if (purpose.length > LOAN_PURPOSE_LIMIT) {
			message.error(
				`Cel pożyczki może mieć maksymalnie ${LOAN_PURPOSE_LIMIT} znaków`,
			);
			setActiveTab("loan");
			return;
		}
		setSaving(true);
		try {
			const shouldAutoGeneratePdf = isNewLoan && !skipPromissory;
			const response = await pozyczkiUpdateData({
				loan,
				borrower,
				user: user?.user?.mlg_users_id,
			});
			if (!response?.data?.status) {
				message.error(
					response?.data?.message || "Nie udało się zapisać pożyczki",
				);
				return;
			}
			const savedLoanId = response?.data?.data?.id;
			if (shouldAutoGeneratePdf && savedLoanId) {
				await downloadPromissoryPdf(savedLoanId, true);
			}
			message.success("Zapisano pożyczkę");
			setModalOpen(false);
			await fetchList();
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - zapis pożyczki");
		} finally {
			setSaving(false);
		}
	};

	const addManualRepayment = async () => {
		if (!loan?.msr_loans_id || isNewLoan) return;

		const settlementDate = String(
			manualRepayment?.settlementDate || "",
		).trim();
		const amount = Number(manualRepayment?.amount);
		const description = String(manualRepayment?.description || "").trim();

		if (!settlementDate) {
			message.error("Data rozliczenia jest wymagana");
			return;
		}

		if (!Number.isFinite(amount) || amount <= 0) {
			message.error("Kwota rozliczenia musi być większa od zera");
			return;
		}

		if (isDateBefore(settlementDate, loan?.msr_loans_date)) {
			message.error(
				"Data rozliczenia nie może być wcześniejsza niż data pożyczki",
			);
			return;
		}

		setManualRepaymentSaving(true);
		try {
			const response = await pozyczkiAddManualRepayment({
				loanId: loan.msr_loans_id,
				settlementDate,
				amount,
				description,
				user: user?.user?.mlg_users_id,
			});

			if (!response?.data?.status) {
				message.error(
					response?.data?.message ||
						"Nie udało się dodać ręcznego rozliczenia",
				);
				return;
			}

			setManualRepayment(createManualRepayment());
			await loadLoanDetails(loan.msr_loans_id);
			message.success("Dodano ręczne rozliczenie");
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - dodawanie ręcznego rozliczenia");
		} finally {
			setManualRepaymentSaving(false);
		}
	};

	const downloadPromissoryPdf = async (loanId, silent = false) => {
		if (!loanId) return;
		setPdfLoading(true);
		try {
			const response = await pozyczkiGeneratePromissoryPdf(loanId);
			const blob = new Blob([response.data], {
				type: "application/pdf",
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `weksel-${loan?.msr_loans_number || loanId}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			if (!silent) {
				message.success("Wygenerowano PDF weksla");
			}
		} catch (error) {
			console.error(error);
			if (!silent) {
				message.error("Nie udało się wygenerować PDF weksla");
			}
		} finally {
			setPdfLoading(false);
		}
	};

	const openImportXmlPicker = () => {
		xmlInputRef.current?.click();
	};

	const onImportXmlSelected = async (event) => {
		const file = event?.target?.files?.[0];
		event.target.value = "";
		if (!file) return;

		setImportLoading(true);
		try {
			const xmlContent = await file.text();
			const response = await pozyczkiImportXml({
				xmlContent,
				user: user?.user?.mlg_users_id,
			});
			if (!response?.data?.status) {
				message.error(
					response?.data?.message || "Nie udało się zaimportować XML",
				);
				return;
			}
			message.success(
				response?.data?.message || "Zaimportowano plik XML pożyczek",
			);
			await fetchList();
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - import XML pożyczek");
		} finally {
			setImportLoading(false);
		}
	};

	const addComment = async () => {
		if (!loan?.msr_loans_id || isNewLoan) return;
		const text = String(newComment || "").trim();
		if (!text) {
			message.error("Komentarz nie może być pusty");
			return;
		}
		setCommentSaving(true);
		try {
			const response = await pozyczkiAddComment({
				loanId: loan.msr_loans_id,
				comment: text,
				user: user?.user?.mlg_users_id,
			});
			if (!response?.data?.status) {
				message.error(
					response?.data?.message || "Nie udało się dodać komentarza",
				);
				return;
			}
			setNewComment("");
			await loadLoanDetails(loan.msr_loans_id);
			message.success("Dodano komentarz");
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - dodawanie komentarza");
		} finally {
			setCommentSaving(false);
		}
	};

	const addPromissoryNote = async () => {
		if (!loan?.msr_loans_id || isNewLoan) return;
		const payload = {
			loanId: loan.msr_loans_id,
			date: newPromissory.date || null,
			user: user?.user?.mlg_users_id,
		};
		if (!payload.date) {
			message.error("Podaj datę nowego weksla");
			return;
		}
		setPromissorySaving(true);
		try {
			const response = await pozyczkiAddPromissoryNote(payload);
			if (!response?.data?.status) {
				message.error(
					response?.data?.message || "Nie udało się dodać weksla",
				);
				return;
			}
			setNewPromissory({ date: null });
			await loadLoanDetails(loan.msr_loans_id);
			message.success("Dodano nowy weksel");
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - dodawanie weksla");
		} finally {
			setPromissorySaving(false);
		}
	};

	const deleteActivePromissoryNote = async () => {
		if (!loan?.msr_loans_id || isNewLoan) return;
		setDeletePromissoryLoading(true);
		try {
			const response = await pozyczkiDeleteActivePromissoryNote(
				loan.msr_loans_id,
			);
			if (!response?.data?.status) {
				message.error(
					response?.data?.message ||
						"Nie udało się usunąć aktywnego weksla",
				);
				return;
			}
			await loadLoanDetails(loan.msr_loans_id);
			message.success("Usunięto aktywny weksel");
		} catch (error) {
			console.error(error);
			message.error("Problem serwera - usuwanie aktywnego weksla");
		} finally {
			setDeletePromissoryLoading(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				title: "Numer",
				dataIndex: "msr_loans_number",
				key: "msr_loans_number",
			},
			{
				title: "Numer własny pożyczki",
				dataIndex: "msr_loans_custom_number",
				key: "msr_loans_custom_number",
				render: (value) => value || "-",
			},
			{
				title: "Data",
				dataIndex: "msr_loans_date",
				key: "msr_loans_date",
			},
			{
				title: "Pożyczkobiorca",
				dataIndex: "msr_loans_borrower",
				key: "msr_loans_borrower",
				render: (_, record) =>
					record?.msr_loans_borrower ||
					record?.msr_loans_borrowers ||
					"-",
			},
			{
				title: "Kwota",
				dataIndex: "msr_loans_amount",
				key: "msr_loans_amount",
				render: (value) => `${formatPrice(value || 0)} zł`,
			},
			{
				title: "Pozostało",
				dataIndex: "msr_loans_remaining_amount",
				key: "msr_loans_remaining_amount",
				render: (value) => `${formatPrice(value || 0)} zł`,
			},
			{
				title: "Sygnatura akt",
				dataIndex: "msr_loans_case_signature",
				key: "msr_loans_case_signature",
				render: (value) => value || "-",
			},
			{
				title: "Status sprawy",
				dataIndex: "msr_loans_case_status",
				key: "msr_loans_case_status",
				render: (value) => value || "-",
			},
			{
				title: "Akcje",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Button onClick={() => openEdit(record.msr_loans_id)}>
							Edytuj
						</Button>
					</Space>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const getRowStyle = (record) => {
		const remaining = Number(record?.msr_loans_remaining_amount || 0);
		if (remaining <= 0) {
			return { background: "#f6ffed" };
		}
		return { background: "#fff2f0" };
	};

	return (
		<div style={{ padding: "20px" }}>
			<div
				style={{
					display: "flex",
					gap: "12px",
					alignItems: "center",
					flexWrap: "wrap",
					marginBottom: "16px",
				}}
			>
				<Input
					placeholder="Szukaj (numer, sygnatura, osoba)"
					style={{ maxWidth: 420 }}
					value={pagination.search}
					onChange={(e) =>
						setPagination((prev) => ({
							...prev,
							search: e.target.value,
							current: 1,
						}))
					}
				/>
				<DatePicker.RangePicker
					allowClear
					placeholder={["Pożyczka od", "Pożyczka do"]}
					value={
						pagination.loanDateFrom || pagination.loanDateTo
							? [
									pagination.loanDateFrom
										? dayjs(pagination.loanDateFrom)
										: null,
									pagination.loanDateTo
										? dayjs(pagination.loanDateTo)
										: null,
								]
							: null
					}
					onChange={(_, dateStrings) => {
						const [from, to] = dateStrings || [];
						setPagination((prev) => ({
							...prev,
							loanDateFrom: from || "",
							loanDateTo: to || "",
							current: 1,
						}));
					}}
				/>
				<DatePicker.RangePicker
					allowClear
					placeholder={["Aneks od", "Aneks do"]}
					value={
						pagination.annexDateFrom || pagination.annexDateTo
							? [
									pagination.annexDateFrom
										? dayjs(pagination.annexDateFrom)
										: null,
									pagination.annexDateTo
										? dayjs(pagination.annexDateTo)
										: null,
								]
							: null
					}
					onChange={(_, dateStrings) => {
						const [from, to] = dateStrings || [];
						setPagination((prev) => ({
							...prev,
							annexDateFrom: from || "",
							annexDateTo: to || "",
							current: 1,
						}));
					}}
				/>
				<DatePicker.RangePicker
					allowClear
					placeholder={["Weksel od", "Weksel do"]}
					value={
						pagination.promissoryNoteDateFrom ||
						pagination.promissoryNoteDateTo
							? [
									pagination.promissoryNoteDateFrom
										? dayjs(
												pagination.promissoryNoteDateFrom,
											)
										: null,
									pagination.promissoryNoteDateTo
										? dayjs(pagination.promissoryNoteDateTo)
										: null,
								]
							: null
					}
					onChange={(_, dateStrings) => {
						const [from, to] = dateStrings || [];
						setPagination((prev) => ({
							...prev,
							promissoryNoteDateFrom: from || "",
							promissoryNoteDateTo: to || "",
							current: 1,
						}));
					}}
				/>
				<Button
					icon={<UploadOutlined />}
					onClick={openImportXmlPicker}
					loading={importLoading}
					disabled={true}
				>
					Import XML
				</Button>
				<Button
					icon={<PlusOutlined />}
					type="primary"
					onClick={openNew}
				>
					Nowa pożyczka
				</Button>
				<input
					ref={xmlInputRef}
					type="file"
					accept=".xml,text/xml,application/xml"
					style={{ display: "none" }}
					onChange={onImportXmlSelected}
				/>
			</div>

			<Table
				rowKey={(r) => r.msr_loans_id}
				loading={loading}
				columns={columns}
				dataSource={rows}
				onRow={(record) => ({
					style: getRowStyle(record),
				})}
				pagination={{
					current: pagination.current,
					pageSize: pagination.pagesize,
					total,
					showSizeChanger: true,
					pageSizeOptions: [10, 20, 50, 100],
				}}
				onChange={(p) =>
					setPagination((prev) => ({
						...prev,
						current: p.current,
						pagesize: p.pageSize,
					}))
				}
			/>

			<Modal
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				title={
					loan?.msr_loans_number
						? `Pożyczka ${loan.msr_loans_number}`
						: "Nowa pożyczka"
				}
				width={980}
				okText="Zapisz"
				cancelText="Anuluj"
				onOk={save}
				confirmLoading={saving}
				footer={(_, { OkBtn, CancelBtn }) => (
					<Space>
						{!isNewLoan && !isPromissorySkipped && (
							<Button
								icon={<FilePdfOutlined />}
								onClick={() =>
									downloadPromissoryPdf(loan?.msr_loans_id)
								}
								loading={pdfLoading}
							>
								Pobierz PDF weksla
							</Button>
						)}
						<CancelBtn />
						<OkBtn />
					</Space>
				)}
				destroyOnClose
			>
				{loan && (
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: "loan",
								label: "Dane pożyczki",
								children: (
									<div
										style={{
											display: "grid",
											gridTemplateColumns:
												"repeat(3, 1fr)",
											gap: 16,
										}}
									>
										<div
											style={{
												gridColumn: "span 3",
												fontWeight: 600,
											}}
										>
											Pożyczkobiorca
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Imię{" "}
												<span
													style={{ color: "#ff4d4f" }}
												>
													*
												</span>
											</div>
											<Input
												value={
													borrower.msr_loan_borrowers_name ||
													""
												}
												onChange={(e) =>
													setBorrower((prev) => ({
														...prev,
														msr_loan_borrowers_name:
															e.target.value,
													}))
												}
												status={
													borrower?.msr_loan_borrowers_name
														? ""
														: "error"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Nazwisko{" "}
												<span
													style={{ color: "#ff4d4f" }}
												>
													*
												</span>
											</div>
											<Input
												value={
													borrower.msr_loan_borrowers_lastname ||
													""
												}
												onChange={(e) =>
													setBorrower((prev) => ({
														...prev,
														msr_loan_borrowers_lastname:
															e.target.value,
													}))
												}
												status={
													borrower?.msr_loan_borrowers_lastname
														? ""
														: "error"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												PESEL
											</div>
											<Input
												value={
													borrower.msr_loan_borrowers_pesel ||
													""
												}
												onChange={(e) =>
													setBorrower((prev) => ({
														...prev,
														msr_loan_borrowers_pesel:
															e.target.value,
													}))
												}
											/>
										</div>

										<div
											style={{
												gridColumn: "span 3",
												height: 1,
												background: "#f0f0f0",
											}}
										/>

										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Numer pożyczki (auto)
											</div>
											<Input
												value={
													loan.msr_loans_number ||
													getLoanAutoNumberPreview(
														loan,
													)
												}
												disabled={true}
												placeholder=""
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Data przyznania
												<span
													style={{ color: "#ff4d4f" }}
												>
													{" "}
													*
												</span>
											</div>
											<DatePicker
												style={{ width: "100%" }}
												value={
													loan.msr_loans_grant_date
														? dayjs(
																loan.msr_loans_grant_date,
																"YYYY-MM-DD",
															)
														: null
												}
												onChange={(d, s) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_grant_date:
															s || null,
													}))
												}
												allowClear
												status={
													loan?.msr_loans_grant_date
														? ""
														: "error"
												}
											/>
										</div>

										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Data pożyczki{" "}
												<span
													style={{ color: "#ff4d4f" }}
												>
													*
												</span>
											</div>
											<DatePicker
												style={{ width: "100%" }}
												value={
													loan.msr_loans_date
														? dayjs(
																loan.msr_loans_date,
																"YYYY-MM-DD",
															)
														: null
												}
												onChange={(d, s) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_date: s,
													}))
												}
												allowClear
												status={
													loan?.msr_loans_date
														? ""
														: "error"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Status sprawy
											</div>
											<Select
												style={{ width: "100%" }}
												options={CASE_STATUS_OPTIONS}
												allowClear
												value={
													loan.msr_loans_case_status ||
													undefined
												}
												onChange={(value) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_case_status:
															value || null,
													}))
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Lokalizacja archiwum
											</div>
											<Input
												value={
													loan.msr_loans_archive_location ||
													""
												}
												onChange={(e) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_archive_location:
															e.target.value,
													}))
												}
											/>
										</div>

										<div style={{ gridColumn: "span 3" }}>
											<Checkbox
												checked={
													Number(
														loan.msr_loans_is_foundation_funding,
													) === 1
												}
												onChange={(e) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_is_foundation_funding:
															e.target.checked
																? 1
																: 0,
													}))
												}
											>
												Czy pożyczka finansowania w
												Fundacji Marshal
											</Checkbox>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Kwota pożyczki{" "}
												<span
													style={{ color: "#ff4d4f" }}
												>
													*
												</span>
											</div>
											<FixedNumberInput
												calculator={true}
												value={loan.msr_loans_amount}
												addonAfter="zł"
												onChange={(value) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_amount: value,
													}))
												}
												status={
													Number(
														loan?.msr_loans_amount,
													) > 0
														? "default"
														: "error"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Aneks (data)
											</div>
											<DatePicker
												style={{ width: "100%" }}
												value={
													loan.msr_loans_annex_date
														? dayjs(
																loan.msr_loans_annex_date,
																"YYYY-MM-DD",
															)
														: null
												}
												onChange={(d, s) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_annex_date:
															s || null,
													}))
												}
												allowClear
											/>
										</div>

										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												<Checkbox
													checked={
														isPromissorySkipped
													}
													onChange={(e) =>
														setLoan((prev) => ({
															...prev,
															msr_loans_skip_promissory_note:
																e.target.checked
																	? 1
																	: 0,
															msr_loans_promissory_note_date:
																e.target.checked
																	? null
																	: prev.msr_loans_promissory_note_date ||
																		dayjs().format(
																			"YYYY-MM-DD",
																		),
															msr_loans_promissory_note_number:
																"",
														}))
													}
												>
													Pomiń weksel dla tej
													pożyczki
												</Checkbox>
											</div>
										</div>

										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Weksel (data)
												{!isPromissorySkipped && (
													<span
														style={{
															color: "#ff4d4f",
														}}
													>
														{" "}
														*
													</span>
												)}
											</div>
											<DatePicker
												style={{ width: "100%" }}
												value={
													loan.msr_loans_promissory_note_date
														? dayjs(
																loan.msr_loans_promissory_note_date,
																"YYYY-MM-DD",
															)
														: null
												}
												onChange={(d, s) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_promissory_note_date:
															s || null,
													}))
												}
												disabled={isPromissorySkipped}
												allowClear
												status={
													isPromissorySkipped ||
													loan?.msr_loans_promissory_note_date
														? ""
														: "error"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Weksel (numer)
											</div>
											<Input
												value={
													(isPromissorySkipped
														? ""
														: loan.msr_loans_promissory_note_number) ||
													getPromissoryAutoNumberPreview(
														loan,
													)
												}
												disabled={true}
												placeholder={
													isPromissorySkipped
														? "Weksel pominięty"
														: "Numer generowany automatycznie"
												}
											/>
										</div>
										<div>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Sygnatura akt
											</div>
											<Input
												value={
													loan.msr_loans_case_signature ||
													""
												}
												onChange={(e) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_case_signature:
															e.target.value,
													}))
												}
											/>
										</div>

										<div style={{ gridColumn: "span 3" }}>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Cel pożyczki
											</div>
											<Input.TextArea
												autoSize={{
													minRows: 2,
													maxRows: 5,
												}}
												maxLength={LOAN_PURPOSE_LIMIT}
												value={
													loan.msr_loans_purpose || ""
												}
												onChange={(e) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_purpose:
															e.target.value,
													}))
												}
											/>
										</div>

										<div style={{ gridColumn: "span 3" }}>
											<div
												style={{
													fontWeight: 500,
													marginBottom: 8,
												}}
											>
												Opis
											</div>
											<Input.TextArea
												autoSize={{
													minRows: 3,
													maxRows: 8,
												}}
												value={
													loan.msr_loans_description ||
													""
												}
												onChange={(e) =>
													setLoan((prev) => ({
														...prev,
														msr_loans_description:
															e.target.value,
													}))
												}
											/>
										</div>
									</div>
								),
							},
							...(!isNewLoan
								? [
										...(!isPromissorySkipped
											? [
													{
														key: "promissory",
														label: "Weksle",
														children: (
															<div
																style={{
																	display:
																		"grid",
																	gridTemplateColumns:
																		"repeat(3, 1fr)",
																	gap: 16,
																}}
															>
																<div>
																	<div
																		style={{
																			fontWeight: 500,
																			marginBottom: 8,
																		}}
																	>
																		Nowy
																		weksel
																		(data)
																	</div>
																	<DatePicker
																		style={{
																			width: "100%",
																		}}
																		value={
																			newPromissory.date
																				? dayjs(
																						newPromissory.date,
																						"YYYY-MM-DD",
																					)
																				: null
																		}
																		onChange={(
																			_,
																			s,
																		) =>
																			setNewPromissory(
																				(
																					prev,
																				) => ({
																					...prev,
																					date:
																						s ||
																						null,
																				}),
																			)
																		}
																		allowClear
																	/>
																</div>
																<div
																	style={{
																		display:
																			"flex",
																		alignItems:
																			"end",
																		gap: 8,
																	}}
																>
																	<Button
																		type="primary"
																		onClick={
																			addPromissoryNote
																		}
																		loading={
																			promissorySaving
																		}
																	>
																		Dodaj
																		nowy
																		weksel
																	</Button>
																	<Popconfirm
																		title="Usunąć aktywny weksel?"
																		onConfirm={
																			deleteActivePromissoryNote
																		}
																		okText="Tak"
																		cancelText="Nie"
																	>
																		<Button
																			danger
																			icon={
																				<DeleteOutlined />
																			}
																			loading={
																				deletePromissoryLoading
																			}
																		>
																			Usuń
																			aktywny
																		</Button>
																	</Popconfirm>
																</div>

																<div
																	style={{
																		gridColumn:
																			"span 3",
																	}}
																>
																	<Table
																		rowKey={(
																			r,
																		) =>
																			r.msr_loan_promissory_notes_id
																		}
																		pagination={
																			false
																		}
																		dataSource={
																			promissoryNotes
																		}
																		onRow={(
																			record,
																		) => ({
																			style:
																				Number(
																					record.msr_loan_promissory_notes_is_active,
																				) ===
																				1
																					? {
																							background:
																								"#f6ffed",
																						}
																					: {
																							color: "#8c8c8c",
																							background:
																								"#fafafa",
																						},
																		})}
																		columns={[
																			{
																				title: "Status",
																				render: (
																					_,
																					r,
																				) =>
																					Number(
																						r.msr_loan_promissory_notes_is_active,
																					) ===
																					1 ? (
																						<Tag color="green">
																							AKTYWNY
																						</Tag>
																					) : (
																						<Tag>
																							ARCHIWALNY
																						</Tag>
																					),
																			},
																			{
																				title: "Data",
																				dataIndex:
																					"msr_loan_promissory_notes_date",
																				render: (
																					v,
																				) =>
																					v ||
																					"-",
																			},
																			{
																				title: "Numer",
																				dataIndex:
																					"msr_loan_promissory_notes_number",
																				render: (
																					v,
																				) =>
																					v ||
																					"-",
																			},
																			{
																				title: "Dodał",
																				render: (
																					_,
																					r,
																				) =>
																					r
																						?.userDetails
																						?.mlg_users_fullname ||
																					"-",
																			},
																			{
																				title: "Utworzono",
																				render: (
																					_,
																					r,
																				) =>
																					r.msr_loan_promissory_notes_created_datetime
																						? dayjs
																								.unix(
																									r.msr_loan_promissory_notes_created_datetime,
																								)
																								.format(
																									"YYYY-MM-DD HH:mm",
																								)
																						: "-",
																			},
																		]}
																	/>
																</div>
															</div>
														),
													},
												]
											: []),
										{
											key: "comments",
											label: "Komentarze",
											children: (
												<div>
													<Input.TextArea
														value={newComment}
														onChange={(e) =>
															setNewComment(
																e.target.value,
															)
														}
														autoSize={{
															minRows: 3,
															maxRows: 6,
														}}
														placeholder="Dodaj komentarz do pożyczki"
													/>
													<div
														style={{
															marginTop: 12,
															display: "flex",
															justifyContent:
																"flex-end",
														}}
													>
														<Button
															type="primary"
															onClick={addComment}
															loading={
																commentSaving
															}
														>
															Dodaj komentarz
														</Button>
													</div>

													<div
														style={{
															marginTop: 16,
															display: "grid",
															gap: 12,
														}}
													>
														{comments.length ===
															0 && (
															<div
																style={{
																	color: "#8c8c8c",
																}}
															>
																Brak komentarzy.
															</div>
														)}
														{comments.map(
															(item) => (
																<div
																	key={
																		item.msr_loan_comments_id
																	}
																	style={{
																		border: "1px solid #f0f0f0",
																		borderRadius: 8,
																		padding: 12,
																	}}
																>
																	<div
																		style={{
																			display:
																				"flex",
																			justifyContent:
																				"space-between",
																			fontSize: 12,
																			color: "#8c8c8c",
																			marginBottom: 8,
																		}}
																	>
																		<span>
																			{item
																				?.userDetails
																				?.mlg_users_fullname ||
																				"-"}
																		</span>
																		<span>
																			{item.msr_loan_comments_created_datetime
																				? dayjs
																						.unix(
																							item.msr_loan_comments_created_datetime,
																						)
																						.format(
																							"YYYY-MM-DD HH:mm",
																						)
																				: "-"}
																		</span>
																	</div>
																	<div>
																		{item.msr_loan_comments_comment ||
																			"-"}
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											),
										},
									]
								: []),
							...(!isNewLoan
								? [
										{
											key: "repayments",
											label: "Historia rozliczeń",
											children: (
												<div>
													<div
														style={{
															display: "grid",
															gridTemplateColumns:
																"repeat(3, 1fr)",
															gap: 16,
															marginBottom: 16,
														}}
													>
														<div>
															<div
																style={{
																	fontWeight: 500,
																	marginBottom: 8,
																}}
															>
																Data rozliczenia
															</div>
															<DatePicker
																style={{
																	width: "100%",
																}}
																value={
																	manualRepayment.settlementDate
																		? dayjs(
																				manualRepayment.settlementDate,
																				"YYYY-MM-DD",
																			)
																		: null
																}
																onChange={(
																	_,
																	s,
																) =>
																	setManualRepayment(
																		(
																			prev,
																		) => ({
																			...prev,
																			settlementDate:
																				s ||
																				"",
																		}),
																	)
																}
															/>
														</div>
														<div>
															<div
																style={{
																	fontWeight: 500,
																	marginBottom: 8,
																}}
															>
																Kwota
																rozliczenia
															</div>
															<FixedNumberInput
																value={
																	manualRepayment.amount
																}
																addonAfter="zł"
																onChange={(
																	value,
																) =>
																	setManualRepayment(
																		(
																			prev,
																		) => ({
																			...prev,
																			amount: value,
																		}),
																	)
																}
															/>
														</div>
														<div
															style={{
																display: "flex",
																alignItems:
																	"end",
															}}
														>
															<Button
																type="primary"
																onClick={
																	addManualRepayment
																}
																loading={
																	manualRepaymentSaving
																}
															>
																Dodaj ręczne
																rozliczenie
															</Button>
														</div>
														<div
															style={{
																gridColumn:
																	"span 3",
															}}
														>
															<div
																style={{
																	fontWeight: 500,
																	marginBottom: 8,
																}}
															>
																Opis rozliczenia
															</div>
															<Input.TextArea
																autoSize={{
																	minRows: 2,
																	maxRows: 4,
																}}
																value={
																	manualRepayment.description
																}
																onChange={(e) =>
																	setManualRepayment(
																		(
																			prev,
																		) => ({
																			...prev,
																			description:
																				e
																					.target
																					.value,
																		}),
																	)
																}
																placeholder="Np. rozliczenie gotówkowe lub korekta ręczna"
															/>
														</div>
													</div>

													<Table
														rowKey={(r) =>
															r.msr_loan_repayments_id
														}
														pagination={false}
														dataSource={repayments}
														columns={[
															{
																title: "Typ",
																render: (
																	_,
																	r,
																) =>
																	Number(
																		r.msr_loan_repayments_is_manual,
																	) === 1 ? (
																		<Tag color="gold">
																			RĘCZNE
																		</Tag>
																	) : (
																		<Tag color="blue">
																			AUTOMATYCZNE
																		</Tag>
																	),
															},
															{
																title: "Data rozliczenia",
																render: (
																	_,
																	r,
																) => {
																	const manualDate =
																		String(
																			r.msr_loan_repayments_settlement_date ||
																				"",
																		).trim();
																	if (
																		manualDate
																	) {
																		return manualDate;
																	}
																	return r.msr_loan_repayments_created_datetime
																		? dayjs
																				.unix(
																					r.msr_loan_repayments_created_datetime,
																				)
																				.format(
																					"YYYY-MM-DD HH:mm",
																				)
																		: "-";
																},
															},
															{
																title: "Kwota",
																dataIndex:
																	"msr_loan_repayments_amount",
																render: (v) =>
																	`${formatPrice(v || 0)} zł`,
															},
															{
																title: "Wpływ",
																dataIndex:
																	"msr_wplywy_own_id",
																render: (
																	v,
																	r,
																) =>
																	v ? (
																		<Button
																			type="link"
																			onClick={() =>
																				r?.msr_wplywy_id
																					? navigate(
																							`/wplywy/${r.msr_wplywy_id}`,
																						)
																					: undefined
																			}
																			style={{
																				padding: 0,
																			}}
																			disabled={
																				!r?.msr_wplywy_id
																			}
																		>
																			#{v}
																		</Button>
																	) : (
																		"-"
																	),
															},
															{
																title: "Osoba",
																render: (
																	_,
																	r,
																) =>
																	`${r.msr_persons_name || ""} ${
																		r.msr_persons_lastname ||
																		""
																	}`.trim() ||
																	"-",
															},
															{
																title: "Opis",
																dataIndex:
																	"msr_loan_repayments_description",
																render: (v) =>
																	v || "-",
															},
															{
																title: "Dodał",
																render: (
																	_,
																	r,
																) =>
																	r
																		?.userDetails
																		?.mlg_users_fullname ||
																	"-",
															},
														]}
													/>
												</div>
											),
										},
									]
								: []),
						]}
					/>
				)}
			</Modal>
		</div>
	);
};

export default Pozyczki;
